"use client";
import { useState } from "react";
import { AlertTriangle, X } from "lucide-react";

import {
    AlertDialog,
    ResponsiveAlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
} from "@/app/_module/components/ui/alert-dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Checkbox } from "@/app/_module/components/ui/checkbox";

interface DNCConfirmationModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    /** blockInbound: also block this lead's calls/texts to the acting agent. */
    onConfirm: (blockInbound?: boolean) => Promise<void>;
    onRecover: () => Promise<void>;
    leadName?: string;
    isRecoveringFromDNC?: boolean; // New prop to indicate if recovering from DNC
    newStatus?: string; // The status being changed to
}

const DNCConfirmationModal = function ({
    isOpen,
    onOpenChange,
    onConfirm,
    onRecover,
    isRecoveringFromDNC = false,
    newStatus,
}: DNCConfirmationModalProps) {
    // "Do Not Call" only stops US contacting THEM. Blocking their inbound calls
    // and texts is a separate, reversible choice offered in the same gesture.
    const [blockInbound, setBlockInbound] = useState(true);

    const handleConfirm = async () => {
        try {
            await onConfirm(blockInbound);
            onOpenChange(false);
        } catch (error) {

            // Keep modal open for retry
        }
    };

    const handleRecover = async () => {
        try {
            await onRecover();
            onOpenChange(false);
        } catch (error) {

            // Keep modal open for retry
        }
    };

    return (
        <AlertDialog onOpenChange={onOpenChange} open={isOpen}>
            <ResponsiveAlertDialogContent size="2xl" className="p-0 border-0 shadow-lg">
                <div className="relative">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                            </div>
                            <AlertDialogTitle className="text-lg font-semibold text-gray-900">
                                DNC Disposition
                            </AlertDialogTitle>
                        </div>
                    </AlertDialogHeader>
                    <Button
                        className="absolute top-4 right-4 h-8 w-8 p-0 hover:bg-gray-100"
                        onClick={() => onOpenChange(false)}
                        size="sm"
                        variant="ghost"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                <AlertDialogDescription className="px-6 pb-4">
                    {isRecoveringFromDNC
                        ? "This lead is currently marked as DNC (Do Not Call). Do you want to recover it?"
                        : "This lead has been marked as DNC (Do Not Call) and requires your attention."}
                </AlertDialogDescription>

                <div className="px-6 pb-4">
                    <div className="text-sm text-gray-700 space-y-3">
                        {isRecoveringFromDNC ? (
                            <>
                                <p>
                                    This lead is currently in <strong>DNC (Do Not Call)</strong> status.
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-gray-600">
                                    <li>Recovering will change status to <strong>{newStatus || 'the new status'}</strong></li>
                                    <li>The lead will be <strong>removed from trash</strong> and returned to active leads</li>
                                    <li>It will no longer auto-delete in 30 days</li>
                                    <li>If you don't recover, it will be deleted in 30 days from trash</li>
                                </ul>
                            </>
                        ) : (
                            <>
                                <p>
                                    This lead has been marked as <strong>DNC (Do Not Call)</strong>.
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-gray-600">
                                    <li>It is automatically removed from the inbox</li>
                                    <li>It will auto-delete in <strong>30 days</strong> from the trash</li>
                                    <li>If you recover this lead, you will be warned that it is a DNC and dialing is at your own risk</li>
                                </ul>
                            </>
                        )}
                    </div>

                    <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                            <span className="text-sm text-gray-700 font-medium">
                                Warning: calls to DNC leads may result in compliance violations.
                            </span>
                        </div>
                    </div>

                    {!isRecoveringFromDNC && (
                        <label className="mt-4 flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-accent/50">
                            <Checkbox
                                checked={blockInbound}
                                onCheckedChange={(v) => setBlockInbound(v === true)}
                                className="mt-0.5"
                            />
                            <span className="text-sm">
                                <span className="font-medium">Also block their calls and texts to me</span>
                                <span className="block text-muted-foreground mt-0.5">
                                    Their calls won&apos;t ring and their texts won&apos;t reach you. Undo any
                                    time in Settings &rarr; Blocked Numbers.
                                </span>
                            </span>
                        </label>
                    )}
                </div>

                <AlertDialogFooter className="px-6 pb-6 pt-0 gap-3 flex-col sm:flex-row">
                    {isRecoveringFromDNC ? (
                        <>
                            <AlertDialogAction asChild className="bg-white text-black">
                                <Button
                                    className="px-4 py-2 whitespace-normal h-auto min-h-[40px] w-full sm:w-auto"
                                    onClick={handleRecover}
                                    variant="outline"
                                >
                                    Leave in DNC (will delete in 30 days)
                                </Button>
                            </AlertDialogAction>
                            <AlertDialogAction asChild>
                                <Button
                                    className="px-4 py-2 whitespace-normal h-auto min-h-[40px] w-full sm:w-auto"
                                    onClick={handleConfirm}
                                >
                                    Recover Lead to {newStatus || 'New Status'}
                                </Button>
                            </AlertDialogAction>
                        </>
                    ) : (
                        <>
                            <AlertDialogAction asChild className="bg-white text-black">
                                <Button
                                    className="px-4 py-2 w-full sm:w-auto"
                                    onClick={handleConfirm}
                                    variant="outline"
                                >
                                    Add to DNC list
                                </Button>
                            </AlertDialogAction>
                            <AlertDialogAction asChild>
                                <Button
                                    className="px-4 py-2 w-full sm:w-auto"
                                    onClick={handleRecover}
                                >
                                    Cancel
                                </Button>
                            </AlertDialogAction>
                        </>
                    )}
                </AlertDialogFooter>
            </ResponsiveAlertDialogContent>
        </AlertDialog>
    );
};

export default DNCConfirmationModal;
